import Vue from "vue";

import * as Sentry from "@sentry/browser";
import { Vue as VueIntegration } from "@sentry/integrations";
import config from "config";
import VueAuth from "@websanova/vue-auth";
import VeeValidate, { Validator } from "vee-validate";
import VueMoment from "vue-moment";
import moment from "moment";
import "moment/locale/pt-br";
import Vue2Filters from "vue2-filters";
import VueMask from "v-mask";

import Axios from "axios";
import VueAxios from "vue-axios";
import Buefy from "buefy/src";

import Router from "./router";
import App from "./App.vue";

// Import Drivers
import HttpDriver from "./helpers/http-driver.js";
import RouterDriver from "@websanova/vue-auth/drivers/router/vue-router.2.x.js";

// Import Components
import ZulInput from "./components/Input.vue";
import ZulTextarea from "./components/Textarea.vue";
import ZulModal from "./components/Modal.vue";
import ZulMegaInput from "./components/MegaInput.vue";
import v_ptBR from "vee-validate/dist/locale/pt_BR";

import { getIdentifierHeaders } from "@zuldigital/utils";

import cityConfig from "./cityConfig";

// Import Validators
import {
  validateCPF,
  validateCNPJ,
  validateDoc,
  validateCreditCard,
  validateZipcodeBr,
} from "./helpers/validators.js";

import ptbr from "./i18n/pt-br.json";
import en from "./i18n/en.json";

// I18n
import VueI18n from "vue-i18n";

const messages = {
  "pt-BR": ptbr,
  en: en,
};

Vue.use(VueI18n);

const lang = localStorage.getItem("zul_app_lang") || "pt-BR";
localStorage.setItem("zul_app_lang", lang);
Axios.defaults.headers.common["Accept-Language"] = lang;

const i18n = new VueI18n({
  locale: lang,
  messages,
});

// Setup Validators
Vue.use(VeeValidate);
if (lang == "pt-BR") {
  Validator.localize("pt_BR", v_ptBR);
}

Vue.use(Buefy);

Validator.extend("cpf", {
  getMessage: (field) => "The " + field + " value is not a valid CPF.",
  validate: validateCPF,
});

Validator.extend("cnpj", {
  getMessage: (field) => "The " + field + " value is not a valid CNPJ.",
  validate: validateCNPJ,
});

Validator.extend("document", {
  getMessage: (field) => "The " + field + " value is not a valid CPF or CNPJ.",
  validate: validateDoc,
});

Validator.extend("credit_card", {
  getMessage: (field) => "The " + field + " value is not a valid Credit Card.",
  validate: validateCreditCard,
});

Validator.extend("zipcode_br", {
  getMessage: (field) =>
    "The " + field + " value is not a valid Brazilian Zip Code.",
  validate: validateZipcodeBr,
});

// Setup Filters
Vue.filter("cpf", function (value) {
  value = value.replace(/[.-]/g, "");
  return (
    value.substr(0, 3) +
    "." +
    value.substr(3, 3) +
    "." +
    value.substr(6, 3) +
    "-" +
    value.substr(9, 2)
  );
});

const zulApp = localStorage.getItem("zul_app");
if (zulApp) {
  let oldConfig = JSON.parse(zulApp);
  let newConfig =
    cityConfig[oldConfig["city"].toLowerCase()] || cityConfig["sao"];

  localStorage.setItem("zul_app", JSON.stringify(newConfig));

  Vue.prototype.app = newConfig;
} else {
  Vue.prototype.app = cityConfig["sao"];
}

// Setup $http
const API = Axios.create({
  baseURL: config.API_URL,
  headers: {
    "X-City": Vue.prototype.app.city,
    "X-Origin": "WEB_APP",
  },
});

API.interceptors.request.use((instance) => {
  instance.headers = {
    ...instance.headers,
    Authorization: "Bearer " + Vue.prototype.$auth.token(),
    ...getIdentifierHeaders(),
  };

  return instance;
});

Vue.use(VueAxios, API);

// Insurance API
Vue.prototype.INSURANCE = Axios.create({
  baseURL: config.INSURANCE_URL,
});
Vue.prototype.INSURANCE.interceptors.request.use((instance) => {
  instance.headers = {
    Authorization: "Bearer " + Vue.prototype.$auth.token(),
    ...getIdentifierHeaders(),
  };
  return instance;
});

// TAX API
Vue.prototype.TAX = Axios.create({
  baseURL: config.TAX_URL,
});
Vue.prototype.TAX.interceptors.request.use((instance) => {
  instance.headers = {
    Authorization: "Bearer " + Vue.prototype.$auth.token(),
    ...getIdentifierHeaders(),
  };
  return instance;
});

// PUBlIC PARKING API
Vue.prototype.PARKING = Axios.create({
  baseURL: config.API_URL,
});
Vue.prototype.PARKING.interceptors.request.use((instance) => {
  instance.headers = {
    Authorization: "Bearer " + Vue.prototype.$auth.token(),
    "X-City": Vue.prototype.app.city,
    ...getIdentifierHeaders(),
  };
  return instance;
});

// REGULARIZATION API
Vue.prototype.REGULARIZATION = Axios.create({
  baseURL: config.REGULARIZATION_URL,
});
Vue.prototype.REGULARIZATION.interceptors.request.use((instance) => {
  instance.headers = {
    Authorization: "Bearer " + Vue.prototype.$auth.token(),
    "X-City": Vue.prototype.app.city,
    ...getIdentifierHeaders(),
  };
  return instance;
});

// Google App Engine
Vue.prototype.GAE = Axios.create({
  baseURL: config.GAE_URL,
  headers: {
    "X-City": Vue.prototype.app.city,
  },
});

// MercadoPago API
Vue.prototype.MP = Axios.create({
  baseURL: "https://api.mercadopago.com",
  headers: {},
});
Vue.prototype.MP.publicKey = config.MERCADO_PAGO_PUBLIC_KEY;

// Declare $router to fix $auth setup
Vue.router = Router;

// Setup $auth
const AuthDriver = {
  request(req, token) {
    clearTimeout(Vue.authTimer);

    this.options.http._setHeaders.call(this, req, {
      Authorization: "Bearer " + token,
      ...getIdentifierHeaders(),
    });
  },
  response(res) {
    const token = (res.headers || {})["x-auth-token"];

    if (token) {
      const base64Url = token.split(".")[1];
      const base64 = base64Url.replace("-", "+").replace("_", "/");

      const JWTData = JSON.parse(window.atob(base64));

      clearTimeout(Vue.authTimer);
      Vue.authTimer = setTimeout(() => {
        location.reload();
      }, (JWTData.exp - Math.round(new Date().getTime() / 1000)) * 1000);
    }

    return token;
  },
};
Vue.use(VueAuth, {
  auth: AuthDriver,
  http: HttpDriver,
  router: RouterDriver,
  rolesVar: "type",
  loginData: {
    url: "auth/login",
    method: "POST",
    redirect: "/dashboard",
    fetchUser: true,
  },
  registerData: { url: "user", method: "POST", redirect: "/dashboard" },
  fetchData: { url: "user", method: "GET", enabled: true },
  parseUserData(data) {
    return data;
  },
  refreshData: { enabled: false },
  authRedirect: { path: "/" },
});

Sentry.init({
  dsn: config.SENTRY_DSN,
  integrations: [
    new VueIntegration({ Vue, attachProps: true, logErrors: true }),
  ],
  environment: config.SENTRY_ENV || config.env,
  release: process.env.VUE_APP_RELEASE_VERSION,
});

Sentry.configureScope((scope) => {
  scope.setExtra("city", Vue.prototype.app.city);
});

// Require the main Sass manifest file
require("./assets/sass/main.scss");

Vue.config.productionTip = false;

// Use Filters
Vue.use(VueMoment, { moment });
Vue.use(Vue2Filters);

Vue.use(VueMask);

// Register components
Vue.component("ZulModal", ZulModal);
Vue.component("ZulInput", ZulInput);
Vue.component("ZulTextarea", ZulTextarea);
Vue.component("ZulMegaInput", ZulMegaInput);

const $root = new Vue({
  i18n: i18n,
  router: Router,
  render: (h) => h(App),
  color: "#FF0000",
}).$mount("#app");

// Opener
window.receiveParameters = (namespace, data) => {
  $root.$emit(`${namespace}-end`, data);
};

function applyConsentScript() {
  const script = document.createElement("script");

  script.setAttribute("id", "zul-consent-script");
  script.setAttribute("type", "text/javascript");
  script.setAttribute("src", config.CONSENT_SCRIPT_URL);

  script.async = true;

  const tag = document.getElementsByTagName("script")[0];
  tag.parentNode.insertBefore(script, tag);
}

applyConsentScript();
